import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPhone, money, pad } from './raffleTokens';
import Utils from '../Utils/Utils';

// Raffle exports. Two different things on purpose (R50):
//   - a single raffle's RECEIPT (with the payout photo — R67/R68, and the
//     tickets that were in the draw — R84);
//   - the whole HISTORY list for the period being viewed.
// Plus the Winners Control reports (§8).

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';
const PHOTO_WIDTH = 480;
const NAVY = [10, 14, 42];

// Upload URLs have no CORS header; the Grails image proxy adds one and
// downsizes the photo, the same route the session PDF uses.
const proxied = (url) => {
  let path;
  try {
    path = new URL(url, window.location.origin).pathname.replace(/^\/+/, '');
  } catch (e) {
    path = String(url).replace(/^\/+/, '');
  }
  return `${API_ENDPOINT}/image/proxy?path=${encodeURIComponent(
    path
  )}&w=${PHOTO_WIDTH}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

async function photoData(raffle) {
  try {
    if (raffle.photoBlob) return await blobToDataUrl(raffle.photoBlob);
    if (!raffle.photoUrl) return null;
    const res = await fetch(proxied(raffle.photoUrl));
    if (!res.ok) return null;
    return await blobToDataUrl(await res.blob());
  } catch (e) {
    return null;
  }
}

const numberText = (r) =>
  r.mode === 'machine'
    ? pad(r.value)
    : `${r.value ?? '—'}${r.colorName ? ` · ${r.colorName}` : ''}`;

const numberWord = (r) =>
  r.mode === 'machine' ? 'Machine' : r.mode === 'auto' ? 'Number' : 'Ticket';

const statusText = (h) =>
  ({
    paid: 'Paid',
    void: `Voided${h.voidReason ? ` · ${h.voidReason}` : ''}`,
    unclaimed: 'No winner claimed',
    carry: 'Pot carried over',
    open: 'In progress',
  }[h.status] || h.status);

// Every raffle PDF is headed by the LOCATION, never the product name — it is
// the location's own record.
function header(doc, locationName, subtitle) {
  const title = locationName || 'Raffle';
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 64, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, 40, 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  if (subtitle) doc.text(subtitle, 40, 50);
  doc.setTextColor(20, 20, 20);
}

async function buildReceipt(raffle, locationName) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  header(doc, locationName, `Raffle #${raffle.raffleNo ?? '—'} · receipt`);

  const rows = [
    [
      'Status',
      raffle.voided || raffle.status === 'void'
        ? `Voided · ${raffle.voidReason || ''}`
        : 'Paid',
    ],
    [
      'Type',
      raffle.mode === 'machine'
        ? 'Machine number'
        : `${raffle.mode === 'auto' ? 'Auto numbers' : 'Ticket'} · ${
            raffle.drawingName || ''
          }`,
    ],
    [numberWord(raffle), numberText(raffle)],
    ...(raffle.poolText
      ? [
          [
            raffle.mode === 'auto'
              ? 'Numbers in the draw'
              : 'Tickets in the draw',
            raffle.poolText,
          ],
        ]
      : []),
    ['Prize', money(raffle.amount)],
    ['Customer', raffle.customerName || '—'],
    [
      'Paid by',
      `${raffle.byName || '—'} · ${raffle.date || ''} ${raffle.time || ''}`,
    ],
    ...(raffle.edited
      ? [
          [
            'Edited',
            `${raffle.edited.byName} · ${raffle.edited.date} ${raffle.edited.time}`,
          ],
        ]
      : []),
  ];
  autoTable(doc, {
    startY: 84,
    body: rows,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 6 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 150, textColor: [91, 100, 120] },
    },
  });

  const img = await photoData(raffle);
  const y = doc.lastAutoTable.finalY + 20;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Payout photo (taken at payment)', 40, y);
  if (img) {
    const props = doc.getImageProperties(img);
    const w = 240;
    const h = (props.height / props.width) * w;
    doc.addImage(img, 'JPEG', 40, y + 10, w, Math.min(h, 400));
  } else {
    doc.setFont('helvetica', 'normal');
    doc.text('Photo unavailable.', 40, y + 20);
  }
  return doc;
}

export async function downloadReceipt(raffle, locationName) {
  const doc = await buildReceipt(raffle, locationName);
  doc.save(
    `raffle-${raffle.raffleNo ?? 'receipt'}-${raffle.date || ''}.pdf`.replace(
      /[\s,]+/g,
      '-'
    )
  );
}

export async function printReceipt(raffle, locationName) {
  const doc = await buildReceipt(raffle, locationName);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
}

/** R50 — the whole list for the period on screen. */
export function downloadHistory({ items, totals, from, to, locationName }) {
  const doc = new jsPDF({
    unit: 'pt',
    format: 'letter',
    orientation: 'landscape',
  });
  const period = from === to ? from : `${from} to ${to}`;
  header(doc, locationName, `Raffle history · ${period}`);

  doc.setFontSize(11);
  doc.text(
    `Paid out ${money(totals.paidAmount)} · ${
      totals.paidCount
    } raffles paid · ${totals.unclaimedCount} unclaimed · ${
      totals.voidCount
    } voided · ${totals.carryCount} carried`,
    40,
    84
  );

  autoTable(doc, {
    startY: 96,
    head: [
      [
        '#',
        'Date',
        'Time',
        'Type',
        'Number',
        'Status',
        'Customer',
        'Prize',
        'By',
        // One column over machine, ticket and auto rows alike, so it stays
        // neutral rather than calling an auto raffle's numbers "tickets".
        'In the draw',
      ],
    ],
    body: items.map((h) => [
      h.raffleNo ? `#${h.raffleNo}` : '—',
      h.businessDay || h.date || '',
      h.time || '',
      h.mode === 'machine' ? 'Machine' : h.drawingName || numberWord(h),
      h.status === 'carry'
        ? `${money(h.potFrom)} → ${money(h.potTo)}`
        : h.value !== null && h.value !== undefined
        ? numberText(h)
        : '—',
      statusText(h),
      h.customerName || '—',
      h.status === 'paid'
        ? money(h.amount)
        : h.status === 'void'
        ? `(${money(h.amount)})`
        : '—',
      h.byName || '',
      h.poolText || '',
    ]),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: NAVY },
  });
  doc.save(`raffle-history-${period.replace(/\s+/g, '-')}.pdf`);
}

export function downloadWinners({
  rows,
  filterLabel,
  windowDays,
  locationName,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  header(
    doc,
    locationName,
    `Raffle winners · last ${windowDays} days · ${filterLabel}`
  );
  autoTable(doc, {
    startY: 84,
    head: [['Customer', 'Wins', 'Total won', 'Flag']],
    body: rows.map((r) => [
      r.customerName || r.customerId,
      r.wins,
      money(r.total),
      r.flagged ? 'Most wins paid by one employee' : '',
    ]),
    styles: { fontSize: 10 },
    headStyles: { fillColor: NAVY },
  });
  doc.save('raffle-winners.pdf');
}

export function downloadCustomerWins({
  customer,
  wins,
  total,
  concentration,
  windowDays,
  locationName,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  header(doc, locationName, `${name} — raffle wins · last ${windowDays} days`);
  doc.setFontSize(11);
  doc.text(`${wins.length} wins · ${money(total)} total`, 40, 84);
  let y = 96;
  if (concentration) {
    doc.setTextColor(180, 106, 0);
    doc.text(
      `${concentration.count} of ${concentration.total} wins were paid by the same employee (${concentration.byName}).`,
      40,
      100
    );
    doc.setTextColor(20, 20, 20);
    y = 112;
  }
  autoTable(doc, {
    startY: y,
    head: [['Date', 'Time', 'Shift', 'Type', 'Number', 'Paid by', 'Amount']],
    body: wins.map((w) => [
      w.date,
      w.time,
      w.shift || '',
      w.type,
      numberText(w),
      w.byName,
      money(w.amount),
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: NAVY },
  });
  doc.save(`raffle-wins-${name.replace(/\s+/g, '-')}.pdf`);
}

// ── The customer's raffle-number strip (§4.5, R95) ──────────────────────
//
// A receipt, not a report: one narrow column the employee tears off and hands
// over. 80mm thermal roll = 226.77pt of paper, and the page is built to the
// exact height its content needs so the printer feeds and cuts right after
// the last line instead of spitting out a letter-sized sheet.
//
// It goes out through Utils.handlePdfAction rather than this file's own
// window.open, so the same button prints from a browser at the counter and
// from the NUVE app on a phone, where the native layer takes the PDF.

const STRIP_W = 226.77; // 80mm
const STRIP_PAD = 10;
const STRIP_INNER = STRIP_W - STRIP_PAD * 2;

const stripDate = (d) =>
  (d instanceof Date ? d : new Date(d || Date.now())).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

/**
 * Draws the strip and returns the height it ended up needing.
 *
 * Called twice: once on a throwaway tall page purely to measure, then on the
 * real document. jsPDF fixes a page's size when the document is created, so
 * the height has to be known up front — and a receipt's length depends
 * entirely on how many numbers the customer is holding.
 */
function layoutStrip(doc, { locationName, customer, group, printedBy }) {
  let y = 18;
  const line = (
    text,
    { size = 9, bold = false, gap = 12, align = 'left' } = {}
  ) => {
    doc.setFont('courier', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.text(text, align === 'center' ? STRIP_W / 2 : STRIP_PAD, y, { align });
    y += gap;
  };
  const rule = (gap = 8) => {
    doc.setDrawColor(140);
    doc.setLineWidth(0.5);
    doc.line(STRIP_PAD, y - 4, STRIP_W - STRIP_PAD, y - 4);
    y += gap;
  };
  // Long names and drawing titles wrap rather than run off the paper.
  const wrapped = (text, opts) =>
    doc
      .splitTextToSize(String(text), STRIP_INNER)
      .forEach((p) => line(p, opts));

  wrapped((locationName || 'Raffle').toUpperCase(), {
    size: 12,
    bold: true,
    align: 'center',
    gap: 14,
  });
  wrapped(group.drawingName || 'Raffle numbers', {
    size: 9,
    align: 'center',
    gap: 11,
  });
  if (group.rollover && group.pot)
    line(`Pot ${money(group.pot)}`, { size: 9, align: 'center', gap: 11 });
  rule();

  wrapped((customer.name || '').toUpperCase(), {
    size: 11,
    bold: true,
    gap: 13,
  });
  if (customer.phone) line(formatPhone(customer.phone), { size: 9, gap: 12 });
  rule();

  line('YOUR RAFFLE NUMBERS', { size: 9, bold: true, gap: 13 });
  if (!group.numbers.length) {
    line('No numbers earned yet.', { size: 9, gap: 12 });
  } else {
    group.numbers.forEach((n) => {
      // Number on the left, when it was earned on the right — the two things
      // the customer checks the strip for.
      doc.setFont('courier', 'bold');
      doc.setFontSize(11);
      doc.text(`#${n.ticketNo}`, STRIP_PAD, y);
      doc.setFont('courier', 'normal');
      doc.setFontSize(9);
      doc.text(`${n.date}  ${n.time}`, STRIP_W - STRIP_PAD, y, {
        align: 'right',
      });
      y += 14;
    });
  }
  rule();

  line(
    `${group.numbers.length} number${
      group.numbers.length === 1 ? '' : 's'
    } this cycle`,
    { size: 9, bold: true, gap: 12 }
  );
  line(stripDate(new Date()), { size: 8, gap: 10 });
  if (printedBy) line(`Issued by ${printedBy}`, { size: 8, gap: 10 });
  wrapped('Keep this slip for the drawing.', { size: 8, gap: 10 });

  return y + 10;
}

export function buildNumbersStrip(data) {
  // Measured on a page of the REAL width, so splitTextToSize wraps exactly as
  // it will on the strip itself; the probe is then thrown away.
  const measured = layoutStrip(
    new jsPDF({ unit: 'pt', format: [STRIP_W, 2000] }),
    data
  );
  // The floor under the height is STRIP_W, and it is load-bearing: jsPDF
  // SWAPS width and height on a portrait page whose width exceeds its
  // height. A customer holding two or three numbers produces a strip
  // shorter than the paper is wide, which came out rotated — 210pt of
  // width on 80mm paper, with the text running off the roll. Never let
  // the page be wider than it is tall; the floor costs a few mm of feed
  // on the shortest strips and nothing on any other.
  const doc = new jsPDF({
    unit: 'pt',
    format: [STRIP_W, Math.max(STRIP_W, measured)],
  });
  layoutStrip(doc, data);
  return doc;
}

export function printNumbersStrip(data) {
  const doc = buildNumbersStrip(data);
  const name = `raffle-numbers-${(data.customer.name || 'customer')
    .replace(/\s+/g, '-')
    .toLowerCase()}.pdf`;
  Utils.handlePdfAction(doc.output('arraybuffer'), 'print', name);
}

export function downloadNumbersStrip(data) {
  const doc = buildNumbersStrip(data);
  doc.save(
    `raffle-numbers-${(data.customer.name || 'customer')
      .replace(/\s+/g, '-')
      .toLowerCase()}.pdf`
  );
}
